import { useEffect, useCallback, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import './tutorial.css';
import { userApi } from '../api/userApi';
import type { AuthResponse } from '../api/authApi';

// Type for Shepherd Tour instance
type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

/**
 * Custom hook for managing the onboarding tutorial using Shepherd.js.
 * 
 * Usage:
 *   const { startTutorial, completeTutorial, isTutorialCompleted } = useTutorial();
 */
export function useTutorial() {
    const tourRef = useRef<ShepherdTour | null>(null);
    const isRunningRef = useRef<boolean>(false); // Prevent duplicate starts

    // Check if tutorial has been completed
    const isTutorialCompleted = useCallback(() => {
        // 1. Check Legacy/Local Flag
        if (localStorage.getItem('cresta_tutorial_completed') === 'true') return true;

        // 2. Check User Object from Backend (saved in localStorage)
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user: AuthResponse = JSON.parse(userStr);
                return user.tutorialCompleted === true;
            }
        } catch (e) {
            console.error('Error parsing user data', e);
        }

        return false;
    }, []);

    // Mark tutorial as completed
    const completeTutorial = useCallback(async () => {
        // 1. Update Legacy/Local Flag (Immediate UI feedback)
        localStorage.setItem('cresta_tutorial_completed', 'true');

        // 2. Update User Object in LocalStorage
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user: AuthResponse = JSON.parse(userStr);
                user.tutorialCompleted = true; // Update flag
                localStorage.setItem('user', JSON.stringify(user));
            }
        } catch (e) {
            console.error('Error updating local user data', e);
        }

        // 3. Persist to Backend
        try {
            await userApi.completeTutorial();
        } catch (e) {
            // Fail silently - we accepted it locally. 
            // In a real app we might want to retry.
            console.warn('Failed to sync tutorial status to backend', e);
        }
    }, []);

    // Reset tutorial (for restart functionality)
    const resetTutorial = useCallback(() => {
        localStorage.removeItem('cresta_tutorial_completed');
        // We don't reset backend state because user just wants to see it again,
        // not "un-complete" it permanently.
    }, []);

    // Initialize the tour
    const initTour = useCallback(() => {
        if (tourRef.current) {
            tourRef.current.complete();
        }

        const tour = new Shepherd.Tour({
            useModalOverlay: true,
            defaultStepOptions: {
                classes: 'cresta-shepherd-step',
                scrollTo: { behavior: 'smooth', block: 'center' },
                cancelIcon: {
                    enabled: true
                },
                buttons: [
                    {
                        text: 'Omitir',
                        action: function () {
                            this.cancel();
                        },
                        classes: 'shepherd-button-secondary'
                    },
                    {
                        text: 'Siguiente',
                        action: function () {
                            this.next();
                        },
                        classes: 'shepherd-button-primary'
                    }
                ]
            }
        });

        // Step 1: Welcome / Dashboard Overview
        tour.addStep({
            id: 'welcome',
            title: '¡Bienvenido a Cresta! 🎉',
            text: `
                <p>Esta es tu plataforma de gestión completa para tu salón.</p>
                <p>Te guiaremos en los <strong>primeros pasos</strong> para que configures todo correctamente.</p>
            `,
            buttons: [
                {
                    text: 'Comenzar Tour',
                    action: function () {
                        this.next();
                    },
                    classes: 'shepherd-button-primary'
                }
            ]
        });

        // Step 2: Configuration (MOST IMPORTANT - First!)
        tour.addStep({
            id: 'configuration',
            title: '⚙️ Paso 1: Configuración Inicial',
            text: `
                <p><strong>¡Importante!</strong> Antes de crear empleados o servicios:</p>
                <ul>
                    <li>📍 Define los datos de tu salón</li>
                    <li>🏷️ Crea <strong>Especialidades</strong> (Corte, Color, etc.)</li>
                    <li>📦 Crea <strong>Categorías de Inventario</strong></li>
                </ul>
                <p style="margin-top: 0.75rem; font-size: 0.875rem; color: #6b7280;">
                    Accede desde tu perfil → Configuración.
                </p>
            `,
            buttons: [
                {
                    text: '👋 Omitir',
                    action: function () { this.cancel(); },
                    classes: 'shepherd-button-secondary'
                },
                {
                    text: '✅ Entendido',
                    action: function () { this.next(); },
                    classes: 'shepherd-button-primary'
                }
            ]
        });

        // Step 3: Employees (need specialties first)
        tour.addStep({
            id: 'employees',
            title: '👔 Paso 2: Tu Equipo',
            text: `
                <p>Agrega a tus estilistas y colaboradores.</p>
                <p>Define sus <strong>especialidades, horarios y comisiones</strong>.</p>
                <p style="margin-top: 0.5rem; font-size: 0.8rem; color: #f59e0b;">
                    ⚠️ Necesitas especialidades primero.
                </p>
            `,
            attachTo: { element: '[href="/app/employees"]', on: 'right' }
        });

        // Step 4: Services (need specialties)
        tour.addStep({
            id: 'services',
            title: '💅 Paso 3: Tus Servicios',
            text: `
                <p>Crea tu catálogo de servicios.</p>
                <p>Define <strong>precios, duración</strong> y qué especialidad requiere cada uno.</p>
            `,
            attachTo: { element: '[href="/app/services"]', on: 'right' }
        });

        // Step 5: Agenda (needs employees + services)
        tour.addStep({
            id: 'agenda',
            title: '📅 Paso 4: Tu Agenda',
            text: `
                <p>Una vez tengas empleados y servicios, ¡ya puedes agendar!</p>
                <p>Ve el calendario por <strong>día, semana o mes</strong>.</p>
            `,
            attachTo: { element: '[href="/app/agenda"]', on: 'right' }
        });

        // Step 6: Clients
        tour.addStep({
            id: 'clients',
            title: '👥 Tus Clientes',
            text: `
                <p>Gestiona tu <strong>CRM</strong> con historial de visitas y preferencias.</p>
                <p>Los clientes se pueden crear "al vuelo" durante una cita.</p>
            `,
            attachTo: { element: '[href="/app/clients"]', on: 'right' }
        });

        // Step 7: Integrations (WhatsApp)
        tour.addStep({
            id: 'integrations',
            title: '💬 Paso 5: WhatsApp',
            text: `
                <p>Conecta tu propio número para enviar <strong>notificaciones automáticas</strong> sin costo.</p>
                <p>Reduce el ausentismo con recordatorios automáticos.</p>
                <p style="margin-top: 0.75rem; font-size: 0.875rem; color: #6b7280;">
                    Ve a Configuración → Integraciones para escanear el QR.
                </p>
            `,
            buttons: [
                {
                    text: 'Siguiente',
                    action: function () {
                        this.next();
                    },
                    classes: 'shepherd-button-primary'
                }
            ]
        });

        // Step 8: Finish
        tour.addStep({
            id: 'finish',
            title: '🚀 ¡Listo!',
            text: `
                <p>Ya conoces el flujo correcto:</p>
                <ol style="padding-left: 1.25rem; margin-top: 0.5rem;">
                    <li>Configuración → Especialidades</li>
                    <li>Agregar Empleados</li>
                    <li>Crear Servicios</li>
                    <li>¡Conectar WhatsApp!</li>
                    <li>¡Agendar Citas!</li>
                </ol>
                <p style="margin-top: 1rem; font-size: 0.875rem; color: #6b7280;">
                    Puedes ver este tour de nuevo desde tu perfil → Ver Tutorial.
                </p>
            `,
            buttons: [
                {
                    text: '¡Comenzar a Configurar!',
                    action: function () {
                        this.complete();
                    },
                    classes: 'shepherd-button-primary'
                }
            ]
        });

        // Event: On complete
        tour.on('complete', () => {
            isRunningRef.current = false;
            completeTutorial();
        });

        // Event: On cancel
        tour.on('cancel', () => {
            isRunningRef.current = false;
            completeTutorial(); // Also mark as completed if skipped
        });

        tourRef.current = tour;
        return tour;
    }, [completeTutorial]);

    // Start the tutorial
    const startTutorial = useCallback((force: boolean = false) => {
        // Prevent starting if already running
        if (isRunningRef.current) {
            console.log('Tutorial already running, skipping duplicate start');
            return;
        }

        // If not forced, check if already completed
        if (!force && isTutorialCompleted()) {
            console.log('Tutorial already completed, skipping start');
            return;
        }

        isRunningRef.current = true;
        const tour = initTour();
        tour.start();
    }, [initTour, isTutorialCompleted]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (tourRef.current) {
                tourRef.current.complete();
            }
        };
    }, []);

    return {
        startTutorial,
        completeTutorial,
        resetTutorial,
        isTutorialCompleted
    };
}

export default useTutorial;
